import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { getDayAnnotations } from '@/utils/islamicEvents';
import { pickLang, RTL_LANGS, COMMON, WEEKDAYS_SHORT, tt } from '@/utils/localizedStrings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DualCalendarWidget - shows BOTH Hijri and Gregorian calendars stacked
 * with cross-references inside each cell.
 *
 * Modes:
 *   "hijri"     - only Hijri calendar
 *   "dual"      - both calendars stacked (Hijri on top, Gregorian below)
 *   "gregorian" - only Gregorian calendar
 */
const DualCalendarWidget = ({ surfaceColors, textColors }) => {
  const { t, i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const isRTL = RTL_LANGS.has(lang);
  const [mode, setMode] = useState('dual'); // 'hijri' | 'dual' | 'gregorian'
  const [hijriOffset, setHijriOffset] = useState(0);
  const [gregOffset, setGregOffset] = useState(0);
  const [hijriData, setHijriData] = useState(null);
  const [gregData, setGregData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Watches the user's LOCAL calendar day. When the wall-clock crosses local
  // midnight this key changes → both fetch effects below re-fire → the
  // "today" indicator moves to the new day without needing a page reload.
  const [todayKey, setTodayKey] = useState(() =>
    new Date().toLocaleDateString('en-CA')
  );
  useEffect(() => {
    const tick = () => {
      const current = new Date().toLocaleDateString('en-CA');
      setTodayKey((prev) => (prev === current ? prev : current));
    };
    const id = setInterval(tick, 60_000); // check every minute
    return () => clearInterval(id);
  }, []);

  // Fetch Hijri month based on offset
  useEffect(() => {
    const fetchHijri = async () => {
      try {
        const params = {};
        // Send the client's LOCAL calendar date so the backend flips "today"
        // at the user's midnight (not UTC midnight).
        params.local_date = todayKey;
        if (hijriOffset !== 0) {
          const current = await axios.get(`${API}/hijri-date`);
          let targetMonth = current.data.month + hijriOffset;
          let targetYear = current.data.year;
          while (targetMonth > 12) { targetMonth -= 12; targetYear += 1; }
          while (targetMonth < 1) { targetMonth += 12; targetYear -= 1; }
          params.year = targetYear;
          params.month = targetMonth;
        }
        const res = await axios.get(`${API}/hijri-month`, { params });
        setHijriData(res.data);
      } catch (e) {
        console.error('Hijri fetch error:', e);
      }
    };
    fetchHijri();
  }, [hijriOffset, todayKey]);

  // Fetch Gregorian month based on offset
  useEffect(() => {
    const fetchGreg = async () => {
      try {
        const today = new Date();
        let targetMonth = today.getMonth() + 1 + gregOffset;
        let targetYear = today.getFullYear();
        while (targetMonth > 12) { targetMonth -= 12; targetYear += 1; }
        while (targetMonth < 1) { targetMonth += 12; targetYear -= 1; }
        const res = await axios.get(`${API}/gregorian-month`, {
          params: {
            year: targetYear,
            month: targetMonth,
            local_date: todayKey,
          }
        });
        setGregData(res.data);
        setLoading(false);
      } catch (e) {
        console.error('Greg fetch error:', e);
        setLoading(false);
      }
    };
    fetchGreg();
  }, [gregOffset, todayKey]);

  if (loading || !hijriData || !gregData) {
    return (
      <div
        className="p-6 md:p-8 rounded-2xl backdrop-blur-2xl border glass-surface"
        style={{ background: surfaceColors.background, borderColor: surfaceColors.border }}
        data-testid="dual-calendar-widget"
      >
        <div style={{ color: textColors.secondary }}>{t('loading')}</div>
      </div>
    );
  }

  const weekdaysPrimary = WEEKDAYS_SHORT[lang] || WEEKDAYS_SHORT.en;
  const weekdaysSecondary =
    lang === 'en' ? WEEKDAYS_SHORT.ar : WEEKDAYS_SHORT.en;

  const isLightMode = textColors.primary !== '#FFFFFF';
  const todayBg = textColors.accent;
  const todayText = isLightMode ? '#FFFFFF' : '#050914';

  // === Build cell grids ===
  const buildHijriCells = () => {
    if (!hijriData) return [];
    const firstDay = hijriData.days[0];
    const startWeekday = new Date(firstDay.gregorian_date).getDay();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    hijriData.days.forEach(d => cells.push({ ...d, type: 'hijri' }));
    return cells;
  };

  const buildGregCells = () => {
    if (!gregData) return [];
    const firstDay = gregData.days[0];
    const startWeekday = new Date(firstDay.gregorian_date).getDay();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    gregData.days.forEach(d => cells.push({ ...d, type: 'greg' }));
    return cells;
  };

  // === Calendar block (reusable) ===
  const CalendarBlock = ({ title, subtitle, cells, primaryKey, getSecondary, prev, next, reset, testIdPrefix }) => (
    <div data-testid={`${testIdPrefix}-block`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <Calendar size={20} style={{ color: textColors.accent }} className="flex-shrink-0" />
          <div className="min-w-0">
            <h3
              className="text-heading text-base md:text-lg truncate"
              style={{ color: textColors.primary }}
              data-testid={`${testIdPrefix}-title`}
            >
              {title}
            </h3>
            {subtitle && (
              <div
                className="text-xs mono-text mt-0.5 truncate"
                style={{ color: textColors.secondary }}
              >
                {subtitle}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button
            onClick={prev}
            className="p-2 rounded-lg transition-all hover:bg-white/10"
            style={{ color: textColors.primary }}
            data-testid={`${testIdPrefix}-prev`}
            aria-label="Previous month"
          >
            {isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
          <button
            onClick={reset}
            className="px-3 py-1 rounded-lg transition-all hover:bg-white/10 text-xs text-caption"
            style={{ color: textColors.secondary }}
            data-testid={`${testIdPrefix}-today`}
          >
            {tt(COMMON.thisMonth, lang)}
          </button>
          <button
            onClick={next}
            className="p-2 rounded-lg transition-all hover:bg-white/10"
            style={{ color: textColors.primary }}
            data-testid={`${testIdPrefix}-next`}
            aria-label="Next month"
          >
            {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
      </div>

      {/* Weekday Headers - bilingual */}
      <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 md:mb-3">
        {weekdaysPrimary.map((label, i) => (
          <div
            key={i}
            className="text-center py-1.5 lg:py-2 flex flex-col items-center justify-center gap-0.5"
            style={{ color: textColors.secondary }}
          >
            <span
              className="text-caption leading-none"
              style={{ fontSize: 'clamp(0.625rem, 0.95vw, 1rem)' }}
            >
              {label}
            </span>
            <span
              className="leading-none font-normal"
              style={{
                opacity: 0.55,
                letterSpacing: '0.02em',
                fontSize: 'clamp(0.5rem, 0.78vw, 0.85rem)'
              }}
            >
              {weekdaysSecondary[i]}
            </span>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {cells.map((cell, i) => {
          if (!cell) {
            return <div key={`e-${i}`} className="aspect-square"></div>;
          }
          const isToday = cell.is_today;
          const primary = cell[primaryKey];
          const secondary = getSecondary(cell);

          // Get Islamic annotations for this cell (hijri month/day required)
          const hijriMonth = cell.hijri_month || (hijriData ? hijriData.month : null);
          const hijriDay = cell.hijri_day || (testIdPrefix === 'hijri' ? primary : null);
          const annotations = (hijriMonth && hijriDay)
            ? getDayAnnotations(hijriMonth, hijriDay, cell.gregorian_date)
            : { isFriday: false, isAyyamAlBeedh: false, event: null };

          // Build a tooltip text for hover
          const tooltipParts = [];
          if (annotations.event) {
            tooltipParts.push(isRTL ? annotations.event.name_ar : annotations.event.name_en);
          }
          if (annotations.isAyyamAlBeedh) {
            tooltipParts.push(tt(COMMON.whiteDaysHint, lang));
          }
          if (annotations.isFriday) {
            tooltipParts.push(tt(COMMON.blessedFriday, lang));
          }
          const tooltipText = tooltipParts.join(' · ');

          return (
            <div
              key={`${cell.type}-${primary}-${i}`}
              className="calendar-day relative aspect-square rounded-lg flex flex-col items-center justify-center cursor-default"
              style={{
                background: isToday
                  ? todayBg
                  : annotations.isFriday
                    ? 'rgba(16, 185, 129, 0.08)'
                    : 'rgba(255, 255, 255, 0.05)',
                color: isToday ? todayText : textColors.primary,
                border: isToday
                  ? `1px solid ${todayBg}`
                  : annotations.event
                    ? `1px solid ${annotations.event.color}66`
                    : '1px solid transparent'
              }}
              data-testid={`${testIdPrefix}-day-${primary}`}
              title={tooltipText || undefined}
            >
              {/* Event dot (top-right corner) */}
              {annotations.event && !isToday && (
                <span
                  className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
                  style={{ background: annotations.event.color }}
                  data-testid={`${testIdPrefix}-event-${primary}`}
                />
              )}

              {/* Ayyam al-Beedh marker (small moon icon, top-left corner) */}
              {annotations.isAyyamAlBeedh && !isToday && (
                <span
                  className="absolute top-1 left-1 text-[8px]"
                  style={{ color: '#E8E8E8', opacity: 0.7 }}
                  data-testid={`${testIdPrefix}-white-day-${primary}`}
                >
                  ●
                </span>
              )}

              <span
                className="tabular-nums font-semibold leading-none"
                style={{ fontSize: 'clamp(0.95rem, 2.1vw, 2.1rem)' }}
              >
                {primary}
              </span>
              <span
                className="tabular-nums leading-none"
                style={{
                  color: isToday
                    ? (isLightMode ? 'rgba(255,255,255,0.85)' : 'rgba(5,9,20,0.7)')
                    : textColors.secondary,
                  opacity: 0.85,
                  fontSize: 'clamp(0.625rem, 1.1vw, 1.1rem)',
                  marginTop: 'clamp(0.15rem, 0.4vw, 0.5rem)'
                }}
              >
                {secondary}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // === Render based on mode ===
  const hijriTitle = `${isRTL ? hijriData.month_name_ar : hijriData.month_name_en} ${hijriData.year} ${tt(COMMON.AH, lang)}`;
  const hijriSubtitle = tt(COMMON.hijriSubtitle, lang);

  const gregTitle = `${isRTL ? gregData.month_name_ar : gregData.month_name_en} ${gregData.year}`;
  const gregSubtitle = tt(COMMON.gregSubtitle, lang);

  return (
    <div
      className="p-5 md:p-8 rounded-2xl backdrop-blur-2xl border glass-surface"
      style={{ background: surfaceColors.background, borderColor: surfaceColors.border }}
      data-testid="dual-calendar-widget"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Mode toggle */}
      <div
        className="inline-flex items-center gap-1 p-1 rounded-xl mb-6"
        style={{
          background: 'rgba(255, 255, 255, 0.06)',
          border: `1px solid ${surfaceColors.border}`
        }}
        data-testid="calendar-mode-toggle"
      >
        {[
          { id: 'hijri', label: tt(COMMON.hijri, lang) },
          { id: 'dual', label: tt(COMMON.both, lang) },
          { id: 'gregorian', label: tt(COMMON.gregorian, lang) }
        ].map(opt => (
          <button
            key={opt.id}
            onClick={() => setMode(opt.id)}
            className="px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm transition-all"
            style={{
              background: mode === opt.id ? textColors.accent : 'transparent',
              color: mode === opt.id
                ? (isLightMode ? '#FFFFFF' : '#050914')
                : textColors.secondary,
              fontWeight: mode === opt.id ? 600 : 400
            }}
            data-testid={`mode-${opt.id}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Hijri Calendar */}
      {(mode === 'hijri' || mode === 'dual') && (
        <CalendarBlock
          title={hijriTitle}
          subtitle={mode === 'dual' ? hijriSubtitle : null}
          cells={buildHijriCells()}
          primaryKey="hijri_day"
          getSecondary={(cell) => new Date(cell.gregorian_date).getDate()}
          prev={() => setHijriOffset(hijriOffset - 1)}
          next={() => setHijriOffset(hijriOffset + 1)}
          reset={() => setHijriOffset(0)}
          testIdPrefix="hijri"
        />
      )}

      {/* Divider between calendars (only in dual mode) */}
      {mode === 'dual' && (
        <div
          className="my-6 border-t flex items-center justify-center"
          style={{ borderColor: surfaceColors.border }}
        >
          <span
            className="text-[10px] text-caption px-3 py-1 rounded-full -mt-3"
            style={{
              background: surfaceColors.background,
              color: textColors.secondary,
              backdropFilter: 'blur(8px)'
            }}
          >
            {tt(COMMON.syncedCalendars, lang)}
          </span>
        </div>
      )}

      {/* Gregorian Calendar */}
      {(mode === 'gregorian' || mode === 'dual') && (
        <CalendarBlockGreg
          title={gregTitle}
          subtitle={mode === 'dual' ? gregSubtitle : null}
          cells={buildGregCells()}
          prev={() => setGregOffset(gregOffset - 1)}
          next={() => setGregOffset(gregOffset + 1)}
          reset={() => setGregOffset(0)}
          isRTL={isRTL}
          lang={lang}
          textColors={textColors}
          surfaceColors={surfaceColors}
          weekdaysPrimary={weekdaysPrimary}
          weekdaysSecondary={weekdaysSecondary}
          todayBg={todayBg}
          todayText={todayText}
          isLightMode={isLightMode}
        />
      )}

      {/* Legend */}
      <div
        className="mt-5 pt-4 border-t space-y-2"
        style={{
          borderColor: surfaceColors.border,
          color: textColors.secondary
        }}
        data-testid="calendar-legend"
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] md:text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: todayBg }} />
            <span className="text-caption">{tt(COMMON.today, lang)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: 'rgba(16, 185, 129, 0.4)' }} />
            <span className="text-caption">{tt(COMMON.friday, lang)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px]" style={{ color: '#E8E8E8' }}>●</span>
            <span className="text-caption">{tt(COMMON.whiteDays, lang)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#10B981' }} />
            <span className="text-caption">{tt(COMMON.eidEvent, lang)}</span>
          </div>
        </div>
        <div className="text-[10px] text-caption opacity-70">
          {tt(COMMON.bigNumHint, lang)}
        </div>
      </div>
    </div>
  );
};

// Gregorian calendar block component (uses gregorian_day as primary, hijri_day as secondary)
const CalendarBlockGreg = ({
  title, subtitle, cells, prev, next, reset,
  isRTL, lang, textColors, surfaceColors, weekdaysPrimary, weekdaysSecondary, todayBg, todayText, isLightMode
}) => (
  <div data-testid="gregorian-block">
    <div className="flex items-center justify-between mb-4 lg:mb-5">
      <div className="flex items-center gap-3 min-w-0">
        <Calendar size={20} style={{ color: textColors.accent }} className="flex-shrink-0" />
        <div className="min-w-0">
          <h3
            className="text-heading truncate"
            style={{
              color: textColors.primary,
              fontSize: 'clamp(1rem, 1.45vw, 1.5rem)'
            }}
            data-testid="gregorian-title"
          >
            {title}
          </h3>
          {subtitle && (
            <div
              className="mono-text mt-0.5 truncate"
              style={{
                color: textColors.secondary,
                fontSize: 'clamp(0.7rem, 0.85vw, 0.95rem)'
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button
          onClick={prev}
          className="p-2 rounded-lg transition-all hover:bg-white/10"
          style={{ color: textColors.primary }}
          data-testid="gregorian-prev"
          aria-label="Previous month"
        >
          {isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        <button
          onClick={reset}
          className="px-3 py-1 rounded-lg transition-all hover:bg-white/10 text-xs text-caption"
          style={{ color: textColors.secondary }}
          data-testid="gregorian-today"
        >
          {tt(COMMON.thisMonth, lang)}
        </button>
        <button
          onClick={next}
          className="p-2 rounded-lg transition-all hover:bg-white/10"
          style={{ color: textColors.primary }}
          data-testid="gregorian-next"
          aria-label="Next month"
        >
          {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>
    </div>

    <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 md:mb-3">
      {weekdaysPrimary.map((label, i) => (
        <div
          key={i}
          className="text-center py-1.5 lg:py-2 flex flex-col items-center justify-center gap-0.5"
          style={{ color: textColors.secondary }}
        >
          <span
            className="text-caption leading-none"
            style={{ fontSize: 'clamp(0.625rem, 0.95vw, 1rem)' }}
          >
            {label}
          </span>
          <span
            className="leading-none font-normal"
            style={{
              opacity: 0.55,
              letterSpacing: '0.02em',
              fontSize: 'clamp(0.5rem, 0.78vw, 0.85rem)'
            }}
          >
            {weekdaysSecondary[i]}
          </span>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-7 gap-1 md:gap-2">
      {cells.map((cell, i) => {
        if (!cell) return <div key={`eg-${i}`} className="aspect-square"></div>;
        const isToday = cell.is_today;
        const annotations = getDayAnnotations(cell.hijri_month, cell.hijri_day, cell.gregorian_date);

        const tooltipParts = [];
        if (annotations.event) {
          tooltipParts.push(isRTL ? annotations.event.name_ar : annotations.event.name_en);
        }
        if (annotations.isAyyamAlBeedh) {
          tooltipParts.push(tt(COMMON.whiteDays, lang));
        }
        if (annotations.isFriday) {
          tooltipParts.push(tt(COMMON.blessedFriday, lang));
        }
        const tooltipText = tooltipParts.join(' · ');

        return (
          <div
            key={`g-${cell.gregorian_day}-${i}`}
            className="calendar-day relative aspect-square rounded-lg flex flex-col items-center justify-center cursor-default"
            style={{
              background: isToday
                ? todayBg
                : annotations.isFriday
                  ? 'rgba(16, 185, 129, 0.08)'
                  : 'rgba(255, 255, 255, 0.05)',
              color: isToday ? todayText : textColors.primary,
              border: isToday
                ? `1px solid ${todayBg}`
                : annotations.event
                  ? `1px solid ${annotations.event.color}66`
                  : '1px solid transparent'
            }}
            data-testid={`greg-day-${cell.gregorian_day}`}
            title={tooltipText || undefined}
          >
            {annotations.event && !isToday && (
              <span
                className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
                style={{ background: annotations.event.color }}
              />
            )}
            {annotations.isAyyamAlBeedh && !isToday && (
              <span
                className="absolute top-1 left-1 text-[8px]"
                style={{ color: '#E8E8E8', opacity: 0.7 }}
              >
                ●
              </span>
            )}
            <span
              className="tabular-nums font-semibold leading-none"
              style={{ fontSize: 'clamp(0.95rem, 2.1vw, 2.1rem)' }}
            >
              {cell.gregorian_day}
            </span>
            <span
              className="tabular-nums leading-none"
              style={{
                color: isToday
                  ? (isLightMode ? 'rgba(255,255,255,0.85)' : 'rgba(5,9,20,0.7)')
                  : textColors.secondary,
                opacity: 0.85,
                fontSize: 'clamp(0.625rem, 1.1vw, 1.1rem)',
                marginTop: 'clamp(0.15rem, 0.4vw, 0.5rem)'
              }}
            >
              {cell.hijri_day}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

export default DualCalendarWidget;
